import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { cssVariable, withBackgroundImage } from 'app/utils/style';
import { round } from 'app/utils/number';
import { partitionArray } from 'app/utils/array';
import { usePlayerNameOverflow } from 'app/hooks/usePlayerNameOverflow';
import { LOL_PLAYERS_ROLE_ORDER } from 'app/config/videogameSpecific.config';
import LoLPlayerRoleIndicator from 'app/components/LoLPlayerRoleIndicator';
import IconWithTooltip from 'app/components/IconWithTooltip';
import Tooltip from 'app/components/Tooltip';
import KDA from 'app/components/KDA';
import HeroesIcon from 'app/components/HeroesIcon';
import { isNumber } from 'app/utils/types';
import LolKillIcon from 'app/icons/lol_kill.svg';
import LolCreepIcon from 'app/icons/lol_creep.svg';
import CaretUpIcon from 'app/icons/caret_up.svg';
import GameTimer from 'app/components/GameTimer';
import { getMap, getStatWinner } from 'app/utils/entities/match_data';
import StatValue from 'app/components/StatValue';
import sharedStyle from 'app/components/MatchesRecap/style.module.scss';
import GoldAdvantageChart from 'app/components/GoldAdvantageChart';
import Error from 'app/components/Error';
import { getCDNUrl } from 'app/utils/api_url';

import { getLolTeamMatchStats, getSortedDrakes, getXAxisPosition, lolDrakeIcon } from './utils';
import style from './style.module.scss';

interface Props {
  game: GameLoL;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
}

const LolMatchRecap: FC<Props> = ({ game, opponent1, opponent2 }) => {
  const { t } = useTranslation();

  const gameMap = getMap(game);

  const { team1PlayerNamesRef, team2PlayerNamesRef, playersNamesOverflow } = usePlayerNameOverflow(
    LOL_PLAYERS_ROLE_ORDER.length
  );

  // These objects are the same, just used to access the opponent depending on its number or its side
  const [blues, reds] = partitionArray(game.opponents, opponent =>
    opponent.side === 'blue' ? 0 : 1
  );
  const blue = blues.at(0);
  const red = reds.at(0);
  const [gameOpponents1, gameOpponents2] = partitionArray(game.opponents, opponent =>
    opponent.id === opponent1?.id ? 0 : 1
  );
  const gameOpponent1 = gameOpponents1.at(0);
  const gameOpponent2 = gameOpponents2.at(0);

  const [blueOpponentInfos, redOpponentInfos] =
    opponent1?.id === blue?.id ? [opponent1, opponent2] : [opponent2, opponent1];

  const xAxisPosition = getXAxisPosition(game.blue_gold_adv.map(({ value }) => value));

  const sortedDrakes = getSortedDrakes(blue, red);

  const { opponent1MatchStats, opponent2MatchStats } = getLolTeamMatchStats(
    gameOpponent1,
    gameOpponent2
  );

  if (game.opponents.length === 0) {
    return <Error type="no-data" />;
  }

  return (
    <>
      <div
        className={sharedStyle.scoresContainer}
        style={gameMap ? withBackgroundImage(getCDNUrl(gameMap.image_url)) : {}}
      >
        <div className={style.goldContainer}>
          {isNumber(gameOpponent1?.gold) && `${round(gameOpponent1.gold / 1000, 2)}k`}
        </div>
        <div className={sharedStyle.killsContainer}>
          {isNumber(gameOpponent1?.kills) && (
            <>
              {gameOpponent1.kills}
              <IconWithTooltip
                Icon={LolKillIcon}
                label=""
                iconClassName={classNames(
                  sharedStyle.reversedIcon,
                  style[gameOpponent1.side ?? 'defaultColor']
                )}
              />
            </>
          )}
        </div>
        <div className={sharedStyle.killsContainer}>
          {isNumber(gameOpponent2?.kills) && (
            <>
              <IconWithTooltip
                Icon={LolKillIcon}
                label=""
                iconClassName={style[gameOpponent2.side ?? 'defaultColor']}
              />
              {gameOpponent2.kills}
            </>
          )}
        </div>
        <div className={style.goldContainer}>
          {isNumber(gameOpponent2?.gold) && `${round(gameOpponent2.gold / 1000, 2)}k`}
        </div>
      </div>
      <div className={sharedStyle.matchStats}>
        <ul className={classNames(sharedStyle.teamStats, style.teamStats)}>
          {opponent1MatchStats.map(stat => (
            <li
              key={`opponent1_${stat.name}`}
              className={classNames({ [style.goldStat]: stat.name === 'gold' })}
            >
              {stat.Icon && (
                <IconWithTooltip
                  Icon={stat.Icon}
                  label=""
                  className={sharedStyle.teamStatsTooltip}
                  iconClassName={classNames(
                    sharedStyle.teamStatIcon,
                    style[gameOpponent1?.side ?? 'defaultColor']
                  )}
                />
              )}
              <StatValue value={stat.value} displayFunction={stat.displayFunction} />
            </li>
          ))}
        </ul>
        <div className={sharedStyle.timerContainer}>
          {game.timer && <GameTimer timer={game.timer} videogame={game.videogame_slug} />}
        </div>
        <ul className={classNames(sharedStyle.teamStats, style.teamStats, style.reversed)}>
          {opponent2MatchStats.map(stat => (
            <li
              key={`opponent2_${stat.name}`}
              className={classNames({ [style.goldStat]: stat.name === 'gold' })}
            >
              {stat.Icon && (
                <IconWithTooltip
                  Icon={stat.Icon}
                  label=""
                  className={sharedStyle.teamStatsTooltip}
                  iconClassName={classNames(
                    sharedStyle.teamStatIcon,
                    style[gameOpponent2?.side ?? 'defaultColor']
                  )}
                />
              )}
              <StatValue value={stat.value} displayFunction={stat.displayFunction} />
            </li>
          ))}
        </ul>
      </div>

      <div className={style.statsContainer}>
        {!!(game.blue_gold_adv.length || sortedDrakes.length) && (
          <GoldAdvantageChart
            goldAdvantage={game.blue_gold_adv}
            goldAdvantageTeamInfo={blueOpponentInfos}
            secondTeamInfo={redOpponentInfos}
            goldAvantageTeam="blue"
          >
            {sortedDrakes.map(drake => {
              const Icon = lolDrakeIcon[drake.type];
              const height = drake.side === 'red' ? xAxisPosition : 100 - xAxisPosition;
              if (Icon) {
                return (
                  <div
                    key={drake.timer}
                    className={classNames(style.drakeIcon, style[drake.side], style[drake.type], {
                      [style.noDashedLine]: height < 15
                    })}
                    style={{
                      left: `${(drake.timer / (game.timer?.timer ?? 1)) * 100}%`,
                      ...cssVariable('height', `${height}px`)
                    }}
                  >
                    <Icon />
                  </div>
                );
              }
              return null;
            })}
          </GoldAdvantageChart>
        )}

        <div className={style.playersContainer}>
          <h4 className={sharedStyle.statsContainerTitle}>
            {t('component_match-recap_moba_players')}
          </h4>
          <ul className={sharedStyle.playersList}>
            {LOL_PLAYERS_ROLE_ORDER.map((role, position) => {
              const team1Player = gameOpponent1?.players[role];
              const team2Player = gameOpponent2?.players[role];
              const [team1PlayerCSWinner, team2PlayerCSWinner] = getStatWinner({
                highlighted: 'max',
                team1Value: team1Player?.creep_score,
                team2Value: team2Player?.creep_score
              });
              const creepScoreDifference =
                team1Player?.creep_score && team2Player?.creep_score
                  ? Math.abs(team1Player.creep_score - team2Player.creep_score)
                  : 0;
              return (
                <li className={sharedStyle.playerLine} key={position}>
                  <div>
                    <Tooltip
                      content={team1Player?.name ?? t('all-unknown')}
                      active={playersNamesOverflow[0][position]}
                    >
                      <div
                        className={sharedStyle.playerName}
                        ref={team1PlayerNamesRef.current[position]}
                      >
                        {team1Player?.name ?? t('all-unknown')}
                      </div>
                    </Tooltip>
                    <div className={style.playerStats}>
                      <KDA playerStats={team1Player ?? {}} />
                      {team1Player?.creep_score && (
                        <Tooltip
                          content={t('component_match-recap_videogame_lol_creep-count')}
                          className={style.creepScoreStat}
                        >
                          <LolCreepIcon className={style.creepIcon} />
                          <span
                            className={classNames(style.creepScore, {
                              [style.creepScoreWinner]: team1PlayerCSWinner,
                              [style.creepScoreLoser]: team2PlayerCSWinner
                            })}
                          >
                            {team1Player.creep_score}
                          </span>
                          {team1PlayerCSWinner && (
                            <span className={style.creepAdvantage}>
                              <CaretUpIcon />
                              {creepScoreDifference}
                            </span>
                          )}
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div className={classNames(sharedStyle.playersRole, style.playersRole)}>
                    <HeroesIcon
                      heroes={team1Player ? [team1Player.champion] : []}
                      hidePercentage
                      iconClassName={sharedStyle.playerHero}
                      placeholderSize={1}
                      videogame="lol"
                    />
                    <LoLPlayerRoleIndicator role={role} className={style.playerRoleIcon} />
                    <HeroesIcon
                      heroes={team2Player ? [team2Player.champion] : []}
                      hidePercentage
                      iconClassName={sharedStyle.playerHero}
                      orientation="reversed"
                      videogame="lol"
                    />
                  </div>
                  <div>
                    <Tooltip
                      content={team2Player?.name ?? t('all-unknown')}
                      active={playersNamesOverflow[1][position]}
                    >
                      <div
                        className={sharedStyle.playerName}
                        ref={team2PlayerNamesRef.current[position]}
                      >
                        {team2Player?.name ?? t('all-unknown')}
                      </div>
                    </Tooltip>
                    <div className={style.playerStats}>
                      {team2Player?.creep_score && (
                        <Tooltip
                          content={t('component_match-recap_videogame_lol_creep-count')}
                          className={style.creepScoreStat}
                        >
                          <LolCreepIcon className={style.creepIcon} />
                          <span
                            className={classNames(style.creepScore, {
                              [style.creepScoreWinner]: team2PlayerCSWinner,
                              [style.creepScoreLoser]: team1PlayerCSWinner
                            })}
                          >
                            {team2Player.creep_score}
                          </span>
                          {team2PlayerCSWinner && (
                            <span className={style.creepAdvantage}>
                              <CaretUpIcon />
                              {creepScoreDifference}
                            </span>
                          )}
                        </Tooltip>
                      )}
                      <KDA playerStats={team2Player ?? {}} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className={style.bansContainer}>
          <h4 className={sharedStyle.statsContainerTitle}>
            {t('component_match-recap_moba_bans')}
          </h4>
          <div className={style.champions}>
            <HeroesIcon
              heroes={gameOpponent1?.bans ?? []}
              placeholderSize={5}
              iconClassName={style.bannedHero}
              hidePercentage
              containerClassName={style.teamBans}
              videogame="lol"
              showName
            />
            <HeroesIcon
              heroes={gameOpponent2?.bans ?? []}
              placeholderSize={5}
              iconClassName={style.bannedHero}
              orientation="reversed"
              hidePercentage
              containerClassName={classNames(style.teamBans, style.reversed)}
              videogame="lol"
              showName
            />
          </div>
        </div>
      </div>
    </>
  );
};

LolMatchRecap.displayName = 'LolMatchRecap';

export default LolMatchRecap;
